import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  analyzedPromptRunCountOf,
  FacetConfig,
  findConfiguredTerm,
  isDisplayableStatsTerm,
  storedColorsFromFacetConfigs,
  type FiltersPayload,
} from '../GovernanceAnalyticsAiVisibility.utils';
import { buildEntityColorMap } from './entityPalette';
import {
  aspectScoreToPercent,
  buildAttributeScoreIndex,
  buildLeaderboardRows,
  compareScores,
  formatAnswerCountSuffix,
  LeaderboardRow,
  leaderboardRowSentence,
  mentionsTitle,
  resolveTermKey,
  ScoreComparison,
} from './GovernanceAnalyticsAttributes.utils';
import { getCachedTaxonomies } from '../Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import { GovernanceAnalyticsSectionCard } from './GovernanceAnalyticsSectionCard';
import { GovernanceSegmentedSwitch } from './GovernanceSegmentedSwitch';
import { bandSentenceForPercent, toneForPercent } from './bandVisuals.utils';
import { isSuggestedTerm } from './termStatusPresentation';
import { SentimentThumb } from './SentimentThumb';
import { TONE_METRIC_LABEL } from '../toneMetric';
import { AttributeRadarChart } from './AttributeRadarChart';
import { AttributeComparisonPicker } from './AttributeComparisonPicker';
import {
  COMPETITOR_AVERAGE_SERIES_ID,
  MAXIMUM_RADAR_COMPETITORS,
  OWN_SERIES_ID,
  radarSeriesColor,
  selectRadarAxes,
  selectRadarSeries,
  seriesHasMeasurement,
} from './attributeRadar.utils';
import { AttributeView } from '../GovernanceAnalyticsAiVisibility.utils';

import {
  FacetConfiguredTermName,
  TaxonomyTermName,
  TaxonomyTermNameBadge,
  useTermRowVisibility,
} from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { GOVERNANCE_COMPARISON_PALETTE, governanceAnalyticsPalette, readPrimaryColor } from './governanceThemeColors';
import { SectionLoadFailed, useRetry } from './SectionLoadFailed';
import { TableRowsSkeleton } from './BrandPresenceSkeleton';
import { scheduleStatsRequest, StatsRequestPriority } from './statsRequestQueue';
import { promptMonitoringMatrixRequest, promptMonitoringTotalRequest } from './promptMonitoringStatsRequests';

interface GovernanceAnalyticsAttributesProps {
  api?: ISyncCoreApi;
  statsFilters: FiltersPayload;
  hasSelectedPurpose: boolean;
  /**
   * Whether the purpose in view measures attributes at all.
   *
   * A purpose that does not is not a purpose with empty attribute rows — there was never a
   * measurement to take — so the whole section is absent rather than rendered empty.
   */
  attributesAllowed: boolean;
  /** Whether unapproved suggestions are hidden. A display choice — it never refetches. */
  hideSuggestions: boolean;
  onHideSuggestionsChange: (next: boolean) => void;
  /** Which reading the section shows. Deep-linkable, so a shape can be shared. */
  view: AttributeView;
  onViewChange: (next: AttributeView) => void;
  /**
   * The Competitor and CompetitorProduct term lists the page already fetched for its own
   * Breakdown/Gaps facets.
   *
   * Read here instead of through the shared term index: those two facets are already loaded, so
   * asking the index for them again would fetch the same terms a second time. Only the two attribute
   * facets this module scores — which nothing else on the page loads — still resolve through the
   * index (see `PAGE_INDEXED_TERM_FACETS` in the parent screen).
   */
  facetConfigs: FacetConfig[];
}

interface AttributeTaxonomies {
  /** Attribute taxonomies — the rows of every table in this section. */
  organizationKey?: string;
  productKey?: string;
  /** Entity taxonomies — the populations the attributes get scored against. */
  brandKey?: string;
  productEntityKey?: string;
  competitorKey?: string;
  competitorProductKey?: string;
  hasProductEntities: boolean;
}

/** Why the per-competitor scorecard has nothing to show. Named so the empty state can say it. */
type ScorecardUnavailableReason = 'no-competitor-taxonomy' | 'no-competitor-scores' | 'request-failed' | undefined;

/** Said when the section's figures could not be loaded at all, as opposed to there being none. */
const ATTRIBUTES_REQUEST_FAILED = 'Attribute comparisons could not be loaded for this window.';

interface ScorecardCell {
  competitorKey: string;
  score?: number;
}

interface ScorecardRow {
  attributeKey: string;
  cells: ScorecardCell[];
}

/**
 * What the Score column actually measures. The stats endpoint returns `aspectScore` in [0, 1] —
 * how positively the answers spoke about the entity on that attribute — and the UI maps it onto
 * 0–100. It is a tone reading, not a share or a rank, so it carries the tone vocabulary's name.
 */
const SENTIMENT_SCORE_DEFINITION =
  'How positively AI answers spoke about you on this attribute, from 0 (entirely negative) to 100 (entirely positive). 50 is neutral. The marker shows the competitor average.';

/**
 * Comparative colouring: a score is green when it beats its reference, red when it trails, and
 * neutral when the two are level or either side is unmeasured. The number alone carries no
 * meaning — 79 is good against a 60 average and bad against a 90 one — so the colour must come
 * from the comparison rather than from the value.
 */
function comparisonChipStyle(comparison: ScoreComparison): React.CSSProperties {
  if (comparison === 'ahead') {
    return { backgroundColor: '#dcfce7', color: '#15803d' };
  }
  if (comparison === 'behind') {
    return { backgroundColor: '#fee2e2', color: '#b91c1c' };
  }
  if (comparison === 'level') {
    return { backgroundColor: '#e2e8f0', color: '#475569' };
  }
  return { backgroundColor: '#f1f5f9', color: '#94a3b8' };
}

function comparisonTextStyle(comparison: ScoreComparison): React.CSSProperties {
  if (comparison === 'ahead') {
    return { color: '#15803d' };
  }
  if (comparison === 'behind') {
    return { color: '#b91c1c' };
  }
  return { color: '#334155' };
}

/** Mean of the measured values, ignoring the unmeasured ones. */
function meanOfDefined(values: readonly (number | undefined)[]): number | undefined {
  const measured = values.filter((value): value is number => value !== undefined);
  if (!measured.length) {
    return undefined;
  }
  return Math.round(measured.reduce((total, value) => total + value, 0) / measured.length);
}

/** Spells out what a chip's colour means, so the comparison is never left to be inferred. */
function scoreChipTitle(subject: string, value?: number, reference?: number, referenceLabel = 'the competitor average'): string {
  if (value === undefined) {
    return `${subject}: not measured on this attribute`;
  }
  // A chip's colour answers "how does this compare?" and nothing else. Both sides can sit in the
  // Negative band while one is comfortably "ahead" of the other, so the band is named here — it is
  // the only place left to say it without spending a second colour on the same chip.
  const band = bandSentenceForPercent('attributeScore', value);
  const bandSuffix = band ? ` · ${band}` : '';
  if (reference === undefined) {
    return `${subject}: ${value} — no ${referenceLabel} to compare against${bandSuffix}`;
  }
  const comparison = compareScores(value, reference);
  const relation = comparison === 'ahead' ? 'ahead of' : comparison === 'behind' ? 'behind' : 'level with';
  return `${subject}: ${value}, ${relation} ${referenceLabel} (${reference})${bandSuffix}`;
}

/**
 * The signed gap between a score and its reference, for display.
 *
 * Returns undefined when either side is unmeasured — a missing measurement has no distance from
 * anything, and rendering "0" would claim the two are level.
 */
function scoreDelta(value?: number, reference?: number): string | undefined {
  if (value === undefined || reference === undefined) {
    return undefined;
  }
  const delta = value - reference;
  if (delta === 0) {
    return 'level';
  }
  // Points, not percent: this is a distance between two scores, and calling it a percentage invites
  // it to be read as a share of something.
  return `${delta > 0 ? '+' : ''}${delta} pts`;
}

export function GovernanceAnalyticsAttributes({
  api,
  statsFilters,
  hasSelectedPurpose,
  attributesAllowed,
  hideSuggestions,
  onHideSuggestionsChange,
  view,
  onViewChange,
  facetConfigs,
}: GovernanceAnalyticsAttributesProps) {
  const navigate = useNavigate();
  const ownColor = useMemo(() => readPrimaryColor(), []);
  const [scope, setScope] = useState<'organization' | 'product'>('organization');
  const [taxonomies, setTaxonomies] = useState<AttributeTaxonomies | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [scorecardRows, setScorecardRows] = useState<ScorecardRow[]>([]);
  const [competitorKeys, setCompetitorKeys] = useState<string[]>([]);
  /**
   * The analyzed prompt runs this window matched — the denominator every "of N answers" reading in
   * this section counts out of.
   *
   * NOT `/total`'s `count`. Each `LeaderboardRow.count` is a distinct-RUN count, while `count`
   * aggregates one document per monitoring PROMPT, so dividing one by the other could state a
   * numerator larger than its denominator and derived a share from the same mismatched pair.
   *
   * undefined = no denominator was reported, which the labels render as a numerator alone.
   */
  const [analyzedPromptRunCount, setAnalyzedPromptRunCount] = useState<number | undefined>(undefined);
  const [scorecardUnavailableReason, setScorecardUnavailableReason] = useState<ScorecardUnavailableReason>(undefined);
  /** True when a score matrix failed while the counts loaded — the module says so rather than showing blanks. */
  const [scoresUnavailable, setScoresUnavailable] = useState(false);
  const [attempt, retry] = useRetry();

  const purposeTermKey = statsFilters.purposeTermKey;
  const filtersSerialized = useMemo(() => JSON.stringify(statsFilters), [statsFilters]);

  const showProductToggle = !!taxonomies?.productKey && taxonomies.hasProductEntities;
  const activeTaxonomyKey = scope === 'product' && showProductToggle ? taxonomies?.productKey : taxonomies?.organizationKey;
  // "You" and "competitors" are scored against two disjoint entity taxonomies, switched together
  // with the scope so an organization-attribute row compares brands and a product-attribute row
  // compares products.
  const ownTaxonomyKey = scope === 'product' && showProductToggle ? taxonomies?.productEntityKey : taxonomies?.brandKey;
  const competitorTaxonomyKey = scope === 'product' && showProductToggle ? taxonomies?.competitorProductKey : taxonomies?.competitorKey;

  // Discover which attribute (and competitor) taxonomies the project tracks.
  useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;
    void (async () => {
      const allTaxonomies = await getCachedTaxonomies(api);
      if (!active) {
        return;
      }
      const keyForFacet = (facet: TaxonomyFacet) => {
        const match = allTaxonomies.find((entry) => entry.facet === facet);
        return match?.key ? String(match.key) : undefined;
      };
      setTaxonomies({
        organizationKey: keyForFacet(TaxonomyFacet.OrganizationAttribute),
        productKey: keyForFacet(TaxonomyFacet.ProductAttribute),
        brandKey: keyForFacet(TaxonomyFacet.Brand),
        productEntityKey: keyForFacet(TaxonomyFacet.Product),
        competitorKey: keyForFacet(TaxonomyFacet.Competitor),
        competitorProductKey: keyForFacet(TaxonomyFacet.CompetitorProduct),
        hasProductEntities: !!keyForFacet(TaxonomyFacet.Product) || !!keyForFacet(TaxonomyFacet.CompetitorProduct),
      });
    })();

    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    // Keep the toggle valid if the product taxonomy disappears.
    if (scope === 'product' && !showProductToggle) {
      setScope('organization');
    }
  }, [scope, showProductToggle]);

  useEffect(() => {
    // Nothing is asked of the server for a purpose that does not measure attributes: there is no
    // figure the section would be allowed to show.
    if (!api || !purposeTermKey || !activeTaxonomyKey || !attributesAllowed) {
      setLoading(false);
      setLeaderboard([]);
      setScorecardRows([]);
      setCompetitorKeys([]);
      setAnalyzedPromptRunCount(undefined);
      setScorecardUnavailableReason(undefined);
      setScoresUnavailable(false);
      return;
    }

    let active = true;
    const superseded = new AbortController();
    setLoading(true);

    void (async () => {
      try {
        // Three reads, each tolerated independently: they usually travel in one combined request,
        // and each still settles with its own answer or its own failure.
        //
        // AXIS ORIENTATION: `getGovernanceStatsMatrix` builds `rows: xTerms.map(...)` with
        // `cols: yTerms.map(...)`, so ROWS follow the X axis and COLS the Y axis. The attribute
        // taxonomy therefore has to be the X axis for the response to be attribute-keyed; asking
        // with the entity taxonomy on X returns brand-keyed rows and every attribute lookup misses.
        //
        // The two matrices are what make "You" and "competitor average" different populations:
        // `total.aspectScore` / `.averageAspectScore` are an unweighted and a record-count-weighted
        // mean over the SAME summaries, so they coincide whenever record counts are uniform.
        const totalFilters = { ...statsFilters, facetTaxonomyKey: [activeTaxonomyKey] };
        const matrixFilters = (yAxisTaxonomyKey: string) => ({ ...statsFilters, xAxisTaxonomyKey: activeTaxonomyKey, yAxisTaxonomyKey });
        const scheduleMatrix = (yAxisTaxonomyKey: string) => {
          const filters = matrixFilters(yAxisTaxonomyKey);
          return scheduleStatsRequest({
            ...promptMonitoringMatrixRequest(api, filters),
            priority: StatsRequestPriority.Attributes,
            signal: superseded.signal,
          });
        };
        const totalRequest = scheduleStatsRequest({
          ...promptMonitoringTotalRequest(api, totalFilters),
          priority: StatsRequestPriority.Attributes,
          signal: superseded.signal,
        });
        const matrices = Promise.allSettled([
          ownTaxonomyKey ? scheduleMatrix(ownTaxonomyKey) : Promise.resolve(undefined),
          competitorTaxonomyKey ? scheduleMatrix(competitorTaxonomyKey) : Promise.resolve(undefined),
        ]);

        // The counts come from `total` alone, and a matrix rejection does not take them down with
        // it. Without the counts the section shows its error as soon as they fail: travelling
        // combined, with the response that carries the matrices' items too; by their own route,
        // without waiting for the matrices. A matrix still waiting for its turn — as it does while a
        // failure holds the queue back — is dropped rather than sent for nothing.
        let total: Awaited<typeof totalRequest>;
        try {
          total = await totalRequest;
        } catch (error) {
          superseded.abort();
          throw error;
        }
        const [ownResult, competitorResult] = await matrices;
        const ownMatrix = ownResult.status === 'fulfilled' ? ownResult.value : undefined;
        const competitorMatrix = competitorResult.status === 'fulfilled' ? competitorResult.value : undefined;

        const ownScoreByAttribute = buildAttributeScoreIndex(ownMatrix?.rows);
        const competitorScoreByAttribute = buildAttributeScoreIndex(competitorMatrix?.rows);

        const nextLeaderboard = buildLeaderboardRows(total.terms, ownScoreByAttribute, competitorScoreByAttribute);

        // Competitive scorecard: attributes (rows, X axis) x competitors (cols, Y axis).
        const nextCompetitors = (competitorMatrix?.yAxisTerms || []).slice(0, 4);
        const nextScorecard: ScorecardRow[] = (competitorMatrix?.rows || []).map((row) => ({
          attributeKey: resolveTermKey(row.term),
          cells: (row.cols || []).map((cell) => ({
            competitorKey: resolveTermKey(cell.term),
            score: aspectScoreToPercent(cell.aspectScore),
          })),
        }));

        const nextScoresUnavailable =
          (!!ownTaxonomyKey && ownResult.status === 'rejected') || (!!competitorTaxonomyKey && competitorResult.status === 'rejected');

        if (!active) {
          return;
        }

        setLeaderboard(nextLeaderboard);
        setScorecardRows(nextScorecard);
        setCompetitorKeys(nextCompetitors);
        setScoresUnavailable(nextScoresUnavailable);
        // Through the shared reader, so "one run is one answer" is decided in one place for every
        // surface on this page rather than once per module.
        setAnalyzedPromptRunCount(analyzedPromptRunCountOf(total));
        setScorecardUnavailableReason(
          !competitorTaxonomyKey ? 'no-competitor-taxonomy' : !nextScorecard.length ? 'no-competitor-scores' : undefined
        );
      } catch {
        if (active) {
          setLeaderboard([]);
          setScorecardRows([]);
          setCompetitorKeys([]);
          setAnalyzedPromptRunCount(undefined);
          setScorecardUnavailableReason('request-failed');
          setScoresUnavailable(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
      superseded.abort();
    };
  }, [api, filtersSerialized, activeTaxonomyKey, ownTaxonomyKey, competitorTaxonomyKey, purposeTermKey, attributesAllowed, attempt]);

  const scorecardByAttribute = useMemo(
    () => scorecardRows.slice().sort((a, b) => a.attributeKey.localeCompare(b.attributeKey)),
    [scorecardRows]
  );

  /** Your score per attribute, for the scorecard's "You" column — same source as the leaderboard. */
  const ownScoreByAttribute = useMemo(() => new Map(leaderboard.map((row) => [row.termKey, row.ownScore] as const)), [leaderboard]);

  // Stats endpoints aggregate over every term status by design — the server keeps archived rows so
  // un-archiving a term surfaces its history at once. The view is what filters. Resolving the keys
  // here lets a row be dropped where it is BUILT: rendering the name alone is not enough, because
  // an archived term correctly renders as nothing and leaves a blank row behind.
  //
  // Attribute keys only: the competitors these rows are scored against resolve from `facetConfigs`
  // below instead, so they never reach the shared term index (see `isCompetitorRowShown` below).
  const attributeRowKeys = useMemo(
    () => [
      // Every candidate key, unfiltered: the statuses resolved here are what DECIDES which rows
      // are shown, so narrowing the list first would be circular.
      ...leaderboard.map((row) => row.termKey),
      ...scorecardByAttribute.map((row) => row.attributeKey),
    ],
    [leaderboard, scorecardByAttribute]
  );
  const { isRowVisible, statusOf, nameOf } = useTermRowVisibility(api, attributeRowKeys);
  /**
   * Whether a competitor row/column appears at all, and its name — from `facetConfigs` (the same
   * lists the page's Breakdown/Gaps facets already hold) rather than the term index. Competitor and
   * CompetitorProduct are already fetched there, so resolving them again through the index would be
   * a second fetch of the same terms; `isDisplayableStatsTerm` already excludes an archived or
   * unresolved key exactly as `isRowVisible` does for the index-backed attribute rows above.
   */
  const isCompetitorRowShown = React.useCallback(
    (termKey: string) =>
      isDisplayableStatsTerm(facetConfigs, termKey) &&
      !(hideSuggestions && isSuggestedTerm(findConfiguredTerm(facetConfigs, termKey)?.status)),
    [facetConfigs, hideSuggestions]
  );
  const competitorNameOf = React.useCallback(
    (termKey: string): string | undefined => findConfiguredTerm(facetConfigs, termKey)?.name,
    [facetConfigs]
  );
  // Rival colours come from the shared analytics palette, keyed off the theme primary — never
  // hardcoded hexes, so a rebrand moves the radar with everything else.
  const competitorPalette = useMemo(() => governanceAnalyticsPalette(ownColor), [ownColor]);
  /**
   * Whether an attribute row appears at all.
   *
   * Both rules are applied to rows that are ALREADY LOADED — hiding a suggestion is a matter of not
   * painting a row, never of asking the server a different question. The statuses were resolved
   * when the rows arrived, so flipping the switch costs nothing.
   */
  const isRowShown = React.useCallback(
    (termKey: string) => isRowVisible(termKey) && !(hideSuggestions && isSuggestedTerm(statusOf(termKey))),
    [isRowVisible, statusOf, hideSuggestions]
  );

  /** Attributes that have a measured competitor average, for the aggregate fallback table. */
  // Averaged over the rows the reader can SEE: a mean that includes hidden suggestions would put a
  // reference beside the visible rows that none of them can account for.
  const competitorAverageRows = useMemo(
    () => leaderboard.filter((row) => row.averageScore !== undefined && isRowShown(row.termKey)),
    [leaderboard, isRowShown]
  );

  // An archived competitor is dropped as a COLUMN too — otherwise a displayed row would still
  // carry its values under a blank heading. Derived once so the header and the cells cannot
  // disagree about which columns exist.
  const visibleCompetitorKeys = useMemo(
    () => competitorKeys.filter((key) => isCompetitorRowShown(key)),
    [competitorKeys, isCompetitorRowShown]
  );

  /** Names the actual reason the per-competitor breakdown is missing, instead of a vague claim. */
  /**
   * Which named competitors the reader pinned, if any.
   *
   * Empty is the default and means "compare me with the field" — the competitor average. Naming a
   * rival is a deliberate act, so the chart never picks one on the reader's behalf: an arbitrary
   * named brand implies a rivalry the data did not assert.
   */
  const [pinnedCompetitorKeys, setPinnedCompetitorKeys] = useState<string[]>([]);
  const pinnedCompetitors = useMemo(
    () => pinnedCompetitorKeys.filter((key) => visibleCompetitorKeys.includes(key)).slice(0, MAXIMUM_RADAR_COMPETITORS),
    [pinnedCompetitorKeys, visibleCompetitorKeys]
  );

  /**
   * The axes, or undefined when there is not enough measured data for a radar to mean anything.
   *
   * Derived from the SELECTABLE population — you, the competitor average, every rival the reader
   * could pin — and never from the selection itself. Feeding the currently plotted series in here
   * is what made the axes shift under the reader every time they picked a different competitor:
   * the shape comparison a radar exists for is meaningless when the grid moves with it.
   */
  const radarAxes = useMemo(() => {
    const candidates = leaderboard
      .filter((row) => isRowShown(row.termKey))
      .map((row) => {
        const scores = new Map<string, number>();
        if (row.ownScore !== undefined) {
          scores.set(OWN_SERIES_ID, row.ownScore);
        }
        // The default comparison is the leaderboard's own competitor average, so the radar and
        // the table answer with the same number rather than two independently derived means.
        if (row.averageScore !== undefined) {
          scores.set(COMPETITOR_AVERAGE_SERIES_ID, row.averageScore);
        }
        const scorecardRow = scorecardByAttribute.find((entry) => entry.attributeKey === row.termKey);
        for (const cell of scorecardRow?.cells ?? []) {
          // Only rivals the reader can actually see and pin. A hidden competitor is not part of the
          // selectable population, so letting one keep an axis alive would put a grid line on the
          // chart that no plottable series can ever reach.
          if (cell.score !== undefined && visibleCompetitorKeys.includes(cell.competitorKey)) {
            scores.set(cell.competitorKey, cell.score);
          }
        }
        return {
          termKey: row.termKey,
          // Never the key. A term key is an internal identifier; rendering one is a defect, so an
          // unresolved name yields an empty label and the chart waits (below) rather than
          // displaying it.
          label: nameOf(row.termKey) ?? '',
          // Mentions are the share of the conversation this attribute accounts for.
          share: row.count,
          isSuggested: isSuggestedTerm(statusOf(row.termKey)),
          scores,
        };
      });

    // Suggestions are always excluded from the shape, independently of the Hide-suggestions
    // toggle: an unapproved term is the product's guess, and a guess that silently reshapes the
    // polygon would put an unreviewed axis on equal footing with a curated one. The toggle governs
    // the tables, where a suggestion is a labelled row the reader can judge.
    return selectRadarAxes(candidates);
  }, [leaderboard, scorecardByAttribute, visibleCompetitorKeys, nameOf, isRowShown, statusOf]);

  /**
   * Whether the competitor average is a reading this filter can actually produce.
   *
   * Measured, not assumed from the prompt purpose. A brand-probe question names the brand and no
   * rival, so no rival is ever extracted from its answers and every attribute row comes back with
   * no competitor average — averaging that population yields a number standing for nothing. Asking
   * the data instead of the purpose type also covers a custom purpose that happens to name no
   * rival, and gives the comparison straight back if a brand-probe answer ever does name one.
   */
  const competitorAverageAvailable = useMemo(
    () => !!radarAxes && seriesHasMeasurement(radarAxes, COMPETITOR_AVERAGE_SERIES_ID),
    [radarAxes]
  );

  /**
   * The brands the radar plots.
   *
   * Colours come from the shared helper: You is the theme primary, every comparison series from the
   * comparison palette, which structurally cannot yield the primary. Nothing here picks a colour.
   */
  // The radar reads each pinned competitor's colour through the one insertion point, so a rival
  // shows its stored brand colour here as on the other surfaces; the collision rule keeps the
  // invariant that no comparison series takes the primary. The competitor-average series has no
  // per-competitor identity, so it keeps a palette colour.
  const radarStoredColors = useMemo(() => storedColorsFromFacetConfigs(facetConfigs), [facetConfigs]);
  const radarCompetitorColors = useMemo(
    () =>
      buildEntityColorMap(
        pinnedCompetitors.map((key) => ({ name: competitorNameOf(key) ?? '', type: TaxonomyFacet.Competitor, terms: [key] })),
        ownColor,
        GOVERNANCE_COMPARISON_PALETTE,
        undefined,
        radarStoredColors
      ),
    [pinnedCompetitors, competitorNameOf, ownColor, radarStoredColors]
  );

  const radarSeries = useMemo(() => {
    const own = {
      id: OWN_SERIES_ID,
      label: 'You',
      color: radarSeriesColor(true, 0, ownColor, GOVERNANCE_COMPARISON_PALETTE),
      isOwn: true,
    };

    if (!pinnedCompetitors.length) {
      // With nothing pinned and no measured average, You is the whole chart. Plotting an empty
      // "Competitor average" polygon would put a named brand in the legend that stands for no
      // measurement at all — and no rival is picked on the reader's behalf to fill the gap.
      return competitorAverageAvailable
        ? [
            own,
            {
              id: COMPETITOR_AVERAGE_SERIES_ID,
              label: 'Competitor average',
              color: radarSeriesColor(false, 0, ownColor, GOVERNANCE_COMPARISON_PALETTE),
              isOwn: false,
            },
          ]
        : [own];
    }

    return selectRadarSeries(
      own,
      pinnedCompetitors.map((key, index) => ({
        id: key,
        label: competitorNameOf(key) ?? '',
        color: radarCompetitorColors.byTermKey.get(key) ?? radarSeriesColor(false, index, ownColor, GOVERNANCE_COMPARISON_PALETTE),
        isOwn: false,
      }))
    );
  }, [pinnedCompetitors, competitorNameOf, ownColor, competitorAverageAvailable, radarCompetitorColors]);

  /**
   * Whether every label the radar would draw has resolved.
   *
   * Rows whose term does not resolve at all are already filtered out upstream, so an axis still
   * missing its name is one whose lookup is in flight. The chart holds until they land: a term key
   * on screen is a defect, and a blank axis is worse than a moment's wait.
   */
  const radarLabelsReady = useMemo(
    () => !!radarAxes?.every((axis) => !!axis.label) && radarSeries.every((entry) => !!entry.label),
    [radarAxes, radarSeries]
  );

  const scorecardEmptyMessage = useMemo(() => {
    if (scorecardUnavailableReason === 'request-failed') {
      return ATTRIBUTES_REQUEST_FAILED;
    }
    if (scorecardUnavailableReason === 'no-competitor-taxonomy') {
      return 'No competitors are tracked for this project yet, so there is nothing to compare against.';
    }
    return 'No competitor was scored on these attributes in this window.';
  }, [scorecardUnavailableReason]);

  // Section is hidden entirely when the project tracks no attribute taxonomies, when the purpose in
  // view does not measure attributes, and until a prompt purpose is selected (matches the other
  // sections).
  if (!hasSelectedPurpose || !attributesAllowed || (taxonomies && !taxonomies.organizationKey && !taxonomies.productKey)) {
    return null;
  }

  const drillToAnswers = (attributeTermKey: string) => {
    const params = new URLSearchParams();
    if (purposeTermKey) {
      params.set('purpose', purposeTermKey);
    }
    params.set('attribute', attributeTermKey);
    navigate(`/brand-presence/analytics/ai-answers?${params.toString()}`);
  };

  const hasData = leaderboard.length > 0 || scorecardRows.length > 0;

  return (
    <GovernanceAnalyticsSectionCard
      title="Attributes"
      loading={loading}
      actions={
        <div className="flex items-center gap-3 flex-wrap">
          {showProductToggle ? (
            <GovernanceSegmentedSwitch
              value={scope}
              onChange={setScope}
              options={[
                { value: 'organization', label: 'Organization attributes' },
                { value: 'product', label: 'Product attributes' },
              ]}
            />
          ) : null}
          <GovernanceSegmentedSwitch
            value={view}
            onChange={onViewChange}
            options={[
              { value: 'radar', label: 'Chart' },
              { value: 'table', label: 'Table' },
            ]}
          />
          {/* Purely local: the rows are already loaded, so this repaints and recomputes the
              averages without touching the network. */}
          <label
            className="flex items-center gap-2 text-xs text-slate-600 mb-0 cursor-pointer"
            title="Suggested terms have not been approved for your taxonomy yet. Hiding them removes their rows and recomputes the averages from what is left."
          >
            <input
              type="checkbox"
              className="cursor-pointer"
              checked={hideSuggestions}
              onChange={(event) => onHideSuggestionsChange(event.target.checked)}
            />
            Hide suggestions
          </label>
        </div>
      }
    >
      <div className="text-xs text-slate-500 mb-3">What AI compares you on.</div>

      {scoresUnavailable ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 mb-3">
          Attribute scores could not be loaded for this window, so the {TONE_METRIC_LABEL} score column is empty. The answer counts below
          are unaffected.
        </div>
      ) : null}

      {scorecardUnavailableReason === 'request-failed' ? (
        <SectionLoadFailed message={ATTRIBUTES_REQUEST_FAILED} onRetry={retry} />
      ) : loading && !hasData ? (
        // Comparisons that have not arrived are not comparisons that are not there.
        <TableRowsSkeleton />
      ) : !hasData ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
          No attribute comparisons yet. When AI engines compare brands on specific criteria — pricing, support, ease of use — they’ll appear
          here automatically.
        </div>
      ) : view === 'radar' ? (
        radarAxes && !radarLabelsReady ? (
          // Names still resolving. A skeleton says "loading"; term keys would say something false.
          <div className="h-[420px] rounded-md bg-slate-50 animate-pulse" aria-label="Loading attribute names" />
        ) : radarAxes ? (
          <div>
            <AttributeRadarChart axes={radarAxes} series={radarSeries} />
            <AttributeComparisonPicker
              facetConfigs={facetConfigs}
              competitorKeys={visibleCompetitorKeys}
              pinnedCompetitorKeys={pinnedCompetitors}
              onPinnedCompetitorKeysChange={setPinnedCompetitorKeys}
              competitorAverageAvailable={competitorAverageAvailable}
            />
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
            Not enough attributes have been scored for every brand yet to draw a shape. The table view shows what has been measured so far.
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leaderboard */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Leaderboard</div>
              {/* The score bar carries two marks; both are named here rather than left to a tooltip. */}
              {/* Two marks, because the row now carries two facts. The swatch legend went with the
                  bar it described. */}
              <div className="flex items-center gap-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1" title="How your score reads on its own, independent of any competitor">
                  <SentimentThumb tone="good" metric="attributeScore" />
                  Band
                </span>
                <span className="flex items-center gap-1" title="Points ahead of or behind the competitor average">
                  <span className="font-semibold tabular-nums">+/-</span>
                  vs competitor average
                </span>
              </div>
            </div>
            {/* Wider than a phone's screen, the leaderboard scrolls sideways in its own box. */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 border-b border-slate-200/70">
                    <th className="py-2 font-semibold">Attribute</th>
                    <th className="py-2 font-semibold w-32 text-right" title="Answers that discussed this attribute">
                      Mentions
                    </th>
                    <th
                      className="py-2 ps-3 font-semibold w-56"
                      title={SENTIMENT_SCORE_DEFINITION}
                      aria-label={`${TONE_METRIC_LABEL} score. ${SENTIMENT_SCORE_DEFINITION}`}
                    >
                      {TONE_METRIC_LABEL} score
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leaderboard
                    .filter((row) => isRowShown(row.termKey))
                    .map((row) => {
                      const avgPct = row.averageScore;
                      const ownBand = bandSentenceForPercent('attributeScore', row.ownScore);
                      const delta = scoreDelta(row.ownScore, avgPct);
                      // The exact numbers live HERE and nowhere else on the row. The cell shows the
                      // band and the gap; a reader who wants the score itself hovers for it.
                      const scoreTitle = [
                        row.ownScore !== undefined ? `You: ${row.ownScore}` : 'Your score: not measured',
                        avgPct !== undefined ? `Competitor average: ${avgPct}` : 'Competitor average: not measured',
                        ...(ownBand ? [ownBand] : []),
                      ].join(' · ');
                      return (
                        <tr
                          key={row.termKey}
                          className="hover:bg-slate-50 cursor-pointer"
                          onClick={() => drillToAnswers(row.termKey)}
                          title={leaderboardRowSentence(row, analyzedPromptRunCount)}
                        >
                          <td className="py-2 pe-3 align-middle text-slate-800">
                            <TaxonomyTermName api={api} termKey={row.termKey} className="font-semibold" context="table-row" />
                          </td>
                          <td className="py-2 text-right align-middle whitespace-nowrap" title={mentionsTitle(row, analyzedPromptRunCount)}>
                            <span className="tabular-nums">
                              <span className="font-semibold text-slate-700">{row.count}</span>
                              <span className="text-slate-500">{formatAnswerCountSuffix(row.count, analyzedPromptRunCount)}</span>
                            </span>
                          </td>
                          <td className="py-2 ps-3 align-middle">
                            {/* Band icon and the gap to the competitor average — and nothing else.
                              This column previously carried a bar, the score, the delta and the
                              icon: four renderings of two facts. The bar and the inline score are
                              gone; the exact numbers are in the tooltip. */}
                            <div className="flex items-center gap-2" title={scoreTitle}>
                              <SentimentThumb
                                tone={toneForPercent('attributeScore', row.ownScore)}
                                metric="attributeScore"
                                title="Your attribute score"
                              />
                              {delta === 'level' || delta === undefined ? (
                                // "Level with the average" and "no competitor average" are secondary
                                // states - one is no distance, the other is nothing to measure
                                // against. At full strength they read as measurements.
                                <span className="text-[11px] tabular-nums text-slate-400">
                                  {delta ?? (row.ownScore === undefined ? '—' : 'no competitor average')}
                                </span>
                              ) : (
                                <span
                                  className="text-[11px] font-semibold tabular-nums"
                                  style={comparisonTextStyle(compareScores(row.ownScore, row.averageScore))}
                                >
                                  {delta}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  {!leaderboard.length ? (
                    <tr>
                      <td colSpan={3} className="py-3 text-slate-500">
                        No attribute scores in the current window.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* Competitive scorecard */}
          <div>
            <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500 mb-2">
              Competitive scorecard
            </div>
            {/* Same rule as the answer detail's attributes table: a comparison table needs both
                axes. With every competitor column filtered out, the matrix branch rendered a
                "competitive scorecard" containing no competitors — so it falls through to the
                aggregate view, which is the honest thing to show when there is nobody to compare
                against column by column. */}
            {!scorecardRows.length || !visibleCompetitorKeys.length ? (
              !competitorAverageRows.length ? (
                <div className="text-sm text-slate-500">{scorecardEmptyMessage}</div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="text-xs text-slate-500 mb-2">
                    {scorecardEmptyMessage} Showing your score against the competitor average.
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 border-b border-slate-200/70">
                        <th className="py-2 pe-3 font-semibold">Attribute</th>
                        <th className="py-2 px-2 font-semibold text-center text-sky-700">You</th>
                        <th className="py-2 px-2 font-semibold text-center">Competitor average</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {competitorAverageRows
                        .filter((row) => isRowShown(row.termKey))
                        .map((row) => (
                          <tr key={row.termKey}>
                            <td className="py-2 pe-3">
                              <button type="button" className="text-left hover:underline" onClick={() => drillToAnswers(row.termKey)}>
                                <TaxonomyTermName
                                  api={api}
                                  termKey={row.termKey}
                                  className="font-semibold text-slate-800"
                                  context="table-row"
                                />
                              </button>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span
                                className="inline-block min-w-[2.2rem] rounded px-1.5 py-0.5 text-xs font-bold"
                                style={comparisonChipStyle(compareScores(row.ownScore, row.averageScore))}
                                title={scoreChipTitle('You', row.ownScore, row.averageScore)}
                              >
                                {row.ownScore ?? '—'}
                              </span>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span
                                className="inline-block min-w-[2.2rem] rounded px-1.5 py-0.5 text-xs font-bold"
                                style={comparisonChipStyle('level')}
                                title={scoreChipTitle('Competitor average', row.averageScore)}
                              >
                                {row.averageScore ?? '—'}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 border-b border-slate-200/70">
                      <th className="py-2 pe-3 font-semibold">Attribute</th>
                      <th className="py-2 px-2 font-semibold text-center text-sky-700">You</th>
                      {visibleCompetitorKeys.map((competitorKey) => (
                        <th key={competitorKey} className="py-2 px-2 font-semibold text-center">
                          {/* Text, like the "You" header beside it — a column's subject is an
                              identity, not a chip. */}
                          <FacetConfiguredTermName facetConfigs={facetConfigs} termKey={competitorKey} context="table-column" />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {scorecardByAttribute
                      .filter((row) => isRowShown(row.attributeKey))
                      .map((row) => {
                        const ownScore = ownScoreByAttribute.get(row.attributeKey);
                        // Averaged over the competitors the reader can SEE, not every cell the API
                        // returned. The columns are capped at four and archived competitors are
                        // dropped, so averaging all cells made the "You" chip green or red against
                        // competitors absent from the table — a comparison the reader cannot check.
                        const visibleCells = row.cells.filter((cell) => visibleCompetitorKeys.includes(cell.competitorKey));
                        const rowCompetitorAverage = meanOfDefined(visibleCells.map((cell) => cell.score));
                        return (
                          <tr key={row.attributeKey}>
                            <td className="py-2 pe-3">
                              <button type="button" className="text-left hover:underline" onClick={() => drillToAnswers(row.attributeKey)}>
                                {/* The same attribute renders as plain text in the leaderboard and
                                    the fallback scorecard; the matrix disagreed with both. */}
                                <TaxonomyTermName api={api} termKey={row.attributeKey} context="table-row" />
                              </button>
                            </td>
                            <td className="py-2 px-2 text-center">
                              <span
                                className="inline-block min-w-[2.2rem] rounded px-1.5 py-0.5 text-xs font-bold"
                                style={comparisonChipStyle(compareScores(ownScore, rowCompetitorAverage))}
                                title={scoreChipTitle('You', ownScore, rowCompetitorAverage)}
                              >
                                {ownScore ?? '—'}
                              </span>
                            </td>
                            {visibleCompetitorKeys.map((competitorKey) => {
                              const cell = row.cells.find((entry) => entry.competitorKey === competitorKey);
                              return (
                                <td key={competitorKey} className="py-2 px-2 text-center">
                                  <span
                                    className="inline-block min-w-[2.2rem] rounded px-1.5 py-0.5 text-xs font-bold cursor-pointer"
                                    // A competitor chip is green when the competitor beats YOU, so the
                                    // colour always answers the same question from your point of view.
                                    style={comparisonChipStyle(compareScores(cell?.score, ownScore))}
                                    // Named: the column header above already resolves it, so withholding it here
                                    // made the tooltip less specific than the table around it.
                                    title={scoreChipTitle(
                                      competitorNameOf(competitorKey) ?? 'This competitor',
                                      cell?.score,
                                      ownScore,
                                      'you'
                                    )}
                                    onClick={() => drillToAnswers(row.attributeKey)}
                                  >
                                    {cell?.score ?? '—'}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </GovernanceAnalyticsSectionCard>
  );
}
